import { QueryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { useInfiniteQuery, useQuery } from "../_app/lib/query";
import { AjaxResponse, IdValueQuery } from "../_app/api";
import {
  clearBillLimit,
  clearBillLimitCostCentre,
  getAssetById,
  getAssets,
  getAssetsByCostCentre,
  getAssetsCsv,
  getAssetsOperationHistory,
  getAssetsOperationHistoryCostCentre,
  getBillLimit,
  getBillLimitCostCentre,
  getBillLimitPrefs,
  getClis,
  getLineAssetClis,
  getMobileAssets,
  getMobileAssetsForCostCentre,
  getMobileAssetsWithEligibility,
  getMobileDetailsByAssetId,
  getMobileNetworks,
  getMobileNetworksForCostCentres,
  getServiceDetails,
  getServiceSettings,
  orderSim,
  orderSimCostCentre,
  performLineTest,
  searchAssets,
  updateBillLimit,
  updateBillLimitCostCentre,
  updateBillLimitPrefs,
  updateTag,
} from "./api";
import {
  Address,
  Asset,
  BillLimitValidity,
  ClearBillLimitParams,
  GetAssetsOperationHistoryResponse,
  GetClisResponse,
  SearchAsset,
  UpdateBillLimitParams,
  UpdateBillLimitPrefsParams,
} from "./types";
import { downloadFile, extractFilenameFromHeaders, isObject } from "../_app/utils";
import { assetFilters, mobileManagementFilters } from "./filterConfig";
import { useFeedbackAlerts } from "../_app/hooks";
import { genericError } from "../_app/utils/text";
import { Page } from "../_app/api/types";
import { AxiosResponse } from "axios";
import { UserType } from "../user-level/types";
import { useContextProvider } from "../context/providers/ContextProvider";

export function useAssets(liveOnly: boolean, page: number, limit: number, queries?: any[], options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["assets", { accountId, liveOnly, page, limit, queries }],
    queryFn: () => getAssets(accountId, liveOnly, page, limit, queries),
    ...options,
  });
}

export function useLiveAssets(page: number, limit: number, queries?: any[], options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["assets-live", { accountId, page, limit, queries }],
    queryFn: () => getAssets(accountId, true, page, limit, queries),
    ...options,
  });
}

export function useAssetsByCostCentre(costCentre: string = "unassigned", page: number, limit: number, options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["assets-by-cost-centre", { accountId, costCentre, page, limit }],
    queryFn: () => getAssetsByCostCentre(accountId, costCentre, page, limit),
    ...options,
  });
}

export function useAssetsFilters() {
  return { data: assetFilters };
}

export function useExportAssets(options?: QueryOptions) {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["export-assets", { accountId }],
    queryFn: () => getAssetsCsv(accountId),
    enabled: false,
    gcTime: 0,

    onError: (err: any) => {
      setFeedbackAlertError((err as AxiosResponse).data?.message || "Cannot download asset export due to an error.");
    },

    onSuccess: (response: AxiosResponse) => {
      const filename = extractFilenameFromHeaders(response) || "asset-export.csv";
      downloadFile(response?.data, filename);
    },

    ...options,
  });
}

export function useMobileManagementFilters() {
  return { data: mobileManagementFilters };
}

export function useClisInfinite(query?: string, options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useInfiniteQuery<GetClisResponse>({
    queryKey: ["clis-inf", { accountId, query }],
    queryFn: ({ pageParam }) => getClis(accountId, query, pageParam as number | undefined),
    initialPageParam: 0,
    getNextPageParam: (lastPage: string[], allPages: string[][]) => {
      if (lastPage.length === 0) {
        return undefined;
      } else {
        return allPages.length;
      }
    },
    ...options,
  });
}

export function useLineAssetsInfinite(pageSize: number = 25, query: string, options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useInfiniteQuery<GetClisResponse>({
    queryKey: ["line-assets-inf", { accountId, query }],
    queryFn: ({ pageParam }) => getLineAssetClis(accountId, pageParam as number | undefined, pageSize, query),
    initialPageParam: 0,
    getNextPageParam: (lastPage: string[], allPages: string[][]) => {
      if (lastPage.length === 0) {
        return undefined;
      } else {
        return allPages.length;
      }
    },
    ...options,
  });
}

export function useSingleAsset(id: string, options = {}) {
  const { setFeedbackAlertError } = useFeedbackAlerts();

  return useQuery({
    queryKey: ["asset", id],
    queryFn: () => getAssetById(id),

    onError: () => {
      setFeedbackAlertError("Could not found asset details");
    },

    ...options,
  });
}

export function useServiceSettings(assetId: string, options = {}) {
  return useQuery({
    queryKey: ["serviceSettings", assetId],
    queryFn: () => getServiceSettings(assetId),
    ...options,
  });
}

export function useServiceDetails(assetId: string, page: number, limit: number, options = {}) {
  return useQuery({
    queryKey: ["serviceDetails", { assetId, page, limit }],
    queryFn: () => getServiceDetails(assetId, page, limit),
    ...options,
  });
}

export function useSingleMobileDetails(assetId: string, options = {}) {
  const { setFeedbackAlertError } = useFeedbackAlerts();

  return useQuery({
    queryKey: ["assetId", assetId],
    queryFn: () => getMobileDetailsByAssetId(assetId),

    onError: () => {
      setFeedbackAlertError("Could not find mobile details");
    },

    ...options,
  });
}

export function useMobileAssets(page: number, limit: number, queries?: any[], clis?: string[], options = {}) {
  const { contextId, userType } = useContextProvider();

  return useQuery({
    queryKey: ["mobile-assets", { page, limit, queries, clis, contextId }],

    queryFn: () => {
      if (userType === UserType.COST_CENTRE) {
        return getMobileAssetsForCostCentre(contextId, page, limit, queries);
      } else {
        return getMobileAssets(
          contextId,
          page,
          limit,
          queries,
          clis?.length ? clis?.reduce((f, s) => `${f},${s}`, "") : undefined,
        );
      }
    },

    ...options,
  });
}

export function useMobileAssetsWithEligibility(page: number, limit: number, queries?: any[], clis?: string[], options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["mobile-assets-with-eligibility", { page, limit, queries, clis, accountId }],

    queryFn: () =>
      getMobileAssetsWithEligibility(
        accountId,
        page,
        limit,
        queries,
        clis?.length ? clis?.reduce((f, s) => `${f},${s}`, "") : undefined,
      ),

    ...options,
  });
}

export function useMobileNetworks(assetIds?: string[], filters?: IdValueQuery[], options = {}) {
  const { contextId, userType } = useContextProvider();

  return useQuery({
    queryKey: ["mobile-networks", { assetIds, filters }],

    queryFn: () => {
      if (userType === UserType.COST_CENTRE) {
        return getMobileNetworksForCostCentres(contextId, assetIds, filters);
      } else {
        return getMobileNetworks(contextId, assetIds, filters);
      }
    },

    ...options,
  });
}

export function useBillLimit(assetIds: any[], page: number, limit: number, queries?: any[], options = {}) {
  const { contextId, userType } = useContextProvider();

  const callback = userType === UserType.COST_CENTRE ? getBillLimitCostCentre : getBillLimit;

  return useQuery({
    queryKey: ["bill-limit", { assetIds, page, limit, queries }],
    queryFn: () => callback(contextId, assetIds, page, limit, queries),
    staleTime: 0,
    ...options,
  });
}

export function useBillLimitPrefs(assetId?: string, options = {}) {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId } = useContextProvider();

  return useQuery({
    queryKey: ["bill-limit-prefs", assetId],
    queryFn: () => getBillLimitPrefs(contextId, assetId),
    ...options,

    onError: () => {
      setFeedbackAlertError(genericError());
    },
  });
}

export function useUpdateBillLimitPrefs(options = {}) {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId } = useContextProvider();

  return useMutation({
    mutationFn: (params: UpdateBillLimitPrefsParams) => updateBillLimitPrefs(contextId, params),
    ...options,

    onError: () => {
      setFeedbackAlertError("Cannot perform update");
    },
  });
}

export function useOrderSim(assetId: string, simType: string, options = {}) {
  const { contextId, userType } = useContextProvider();

  return useMutation({
    mutationFn: (address: Address) =>
      userType === UserType.COST_CENTRE
        ? orderSimCostCentre(contextId, assetId, address, simType)
        : orderSim(assetId, address, simType),

    ...options,
  });
}

export function useUpdateBillLimitNextMonth(options: any = {}) {
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();
  const { contextId, userType } = useContextProvider();
  const callback = userType === UserType.COST_CENTRE ? updateBillLimitCostCentre : updateBillLimit;

  return useMutation({
    mutationFn: (params: UpdateBillLimitParams) => callback(contextId, BillLimitValidity.NEXT_MONTH, params),
    ...options,

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message || genericError());
    },

    onSuccess: () => {
      setFeedbackAlertSuccess("Bill limit updated.");
      options?.onSuccess?.();
    },
  });
}

export function useUpdateCurrentBillLimit(options: any = {}) {
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();
  const { contextId, userType } = useContextProvider();
  return useMutation({
    mutationFn: (params: UpdateBillLimitParams) => {
      if (userType === UserType.COST_CENTRE) {
        return Promise.reject({ data: { message: "Operation is not allowed for Cost Centre users." } } as AjaxResponse);
      }
      return updateBillLimit(contextId, BillLimitValidity.CURRENT, params);
    },

    ...options,

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message || genericError());
    },

    onSuccess: () => {
      setFeedbackAlertSuccess("Bill limit updated.");
      options?.onSuccess?.();
    },
  });
}

export function useClearBillLimit(options: any = {}) {
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();
  const { contextId, userType } = useContextProvider();
  const callback = userType === UserType.COST_CENTRE ? clearBillLimitCostCentre : clearBillLimit;

  return useMutation({
    mutationFn: (params: ClearBillLimitParams) => callback(contextId, params),
    ...options,

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message || genericError());
    },

    onSuccess: () => {
      setFeedbackAlertSuccess("Bill limit cleared.");
      options?.onSuccess?.();
    },
  });
}

export function useUpdateTag(options: any = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertSuccess, setFeedbackAlertError } = useFeedbackAlerts();
  return useMutation<any, AjaxResponse, any>({
    mutationFn: updateTag,
    ...options,
    onSuccess: (data: any, variables: any) => {
      queryClient.setQueryData(["asset", variables?.assetId], (old: any) => {
        return {
          ...(isObject(old) ? old : {}),
          tag: variables?.tag,
        };
      });
      queryClient
        .getQueryCache()
        .findAll({
          queryKey: ["assets"],
        })
        .forEach(({ queryKey }) => {
          queryClient.setQueryData(queryKey, (data: any) => {
            const list = data?.list;
            const i = list?.findIndex((item: Asset) => item?.id === variables.assetId);
            if (i >= 0) {
              list[i] = {
                ...list[i],
                tag: variables?.tag,
              };
            }
            return data;
          });
        });
      options?.onSuccess?.();
      setFeedbackAlertSuccess("Tag updated.");
    },
    onError: (error: any) => setFeedbackAlertError(error?.data?.message || genericError()),
  });
}

export function useAssetsOperationHistoryInfinite(limit: number = 25, options = {}) {
  const { userType, contextId } = useContextProvider();
  const callback = userType === UserType.COST_CENTRE ? getAssetsOperationHistoryCostCentre : getAssetsOperationHistory;

  return useInfiniteQuery<GetAssetsOperationHistoryResponse>({
    queryKey: ["assets-operation-history-inf"],
    queryFn: ({ pageParam }) => callback(contextId, pageParam as string | undefined, limit),
    initialPageParam: undefined,
    getNextPageParam: (lastPage: any) => {
      return lastPage?.nextPageToken || undefined;
    },
    staleTime: 0,
    ...options,
  });
}

export function usePerformLineTest() {
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: (cli: string) => performLineTest(accountId, cli),
  });
}

export function useAssetSearch(searchTerm: string, options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useInfiniteQuery({
    queryKey: ["assets-options", { accountId, searchTerm }],
    queryFn: ({ pageParam }) => searchAssets(accountId, pageParam as number, searchTerm),
    initialPageParam: undefined,
    getNextPageParam: (response: Page<SearchAsset>) => {
      return !response.last ? response.number + 1 : undefined;
    },
    ...options,
  });
}
